import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, ListSubheader, Menu, MenuItem, Popover,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as DoneIcon,
  ArrowDropDown as CaretIcon,
  ChevronLeft as LeftIcon,
  ChevronRight as RightIcon,
  ChatBubbleOutlineOutlined as MessageIcon,
  PhoneOutlined as PhoneIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import type {
  Candidate, CandidateOutcome, CandidateStage, Checkpoint, CheckpointStatus,
} from '../../api/services/hiring.service';
import { STAGE_ORDER } from '../../api/services/hiring.service';
import { status as statusTokens, statusLight } from '../../theme';
import EditableCell from './EditableCell';
import {
  GROUP_ROW_HEIGHT, HEAD_BG, HEAD_ROW_HEIGHT, HEAD_TEXT, ROW_HEIGHT,
} from '../../components/ui/tableStyle';
import ScheduleCall from './ScheduleCall';
import {
  STAGE_LABEL, STAGE_COLOR, TRACK_LABEL, OUTCOME_LABEL, candidateName, checkpointLabel, formatDate,
  stepStatus,
} from './hiringUI';

// The tracker's grid, back — by request, and on the website's terms.
//
// Every candidate one row, every step one cell, edited where it is read.
// The identity columns stay put while the steps scroll, and the whole
// thing scrolls inside its own container, never the page. Many rounds of
// the owner's feedback are baked in; the ones that shape the layout:
//
//  - Headers are the checkpoints' real names, whole words, wrapped on as
//    many lines as they take, and everything is centred except the name
//    column — which is sized to the longest name in the pipeline, so no
//    name ever ellipsizes.
//  - The bands wear their stage's colour, the same colour the edge strip
//    speaks, so CHECKS/PAPERWORK/TRAINING read as Clearance's violet at
//    a glance and the code teaches itself.
//  - Cells carry status as a tint: green done, amber underway. A cell
//    that is underway with no NAMED state says "In progress" — it means
//    the old sheet recorded this step as moving ("Processing") without
//    saying which stop it had reached; picking a real state resolves
//    it. ("Part-way" was the first word for this, and had to be asked
//    about, which is the test a word here fails.)
//  - Tooltips only where a cell's behaviour is not obvious: documents
//    (a file is what makes them green), values, field-backed, and
//    does-not-apply. A tick that says "click when it is done" on hover
//    is explaining a checkbox.
//  - Selection is quiet: the checkbox surfaces on hover, and once
//    anybody is ticked they all surface, because a half-made selection
//    needs to be seen whole. Ticked rows take checkpoint edits together,
//    and the bar offers the other things done to many people at once —
//    close as, delete (with the confirm doing the explaining: deleting
//    is for rows that should never have existed).
//  - Status (né Stage) is a grouped dropdown: "Pipeline — moves on its
//    own", where a pick is a manual placement, and "Close as…", which
//    ends the pipeline. Hired is absent: hiring creates an account and
//    has its own flow.
//  - Headers sort on plain clicks; the caret (or right-click) opens the
//    Excel-style filter — quick chips, per-state checkboxes with
//    counts, no Apply button. Paddles page the steps half a screen at a
//    time. Start date, Notes, then Last touched, close the row.

interface Cell {
  status: CheckpointStatus;
  state?: string | null;
  value?: string | null;
}

const CELL = 108;    // px — a glyph column, wide enough for its short name on one line
const WIDE = 148;   // px — a dropdown column
const ROW = ROW_HEIGHT; // px — one candidate (the site's row)
const NUM_W = 52;   // px — the row-number column, frozen with the name
const BAND_H = GROUP_ROW_HEIGHT; // px — the band row; the column row pins to this exact offset

// The column names, short enough for one line of the site's header text.
// The full name is the tooltip, and stays everywhere else.
const SHORT_LABEL: Record<string, string> = {
  outreach_sent: 'Outreach',
  reply_received: 'Reply',
  interview_scheduled: 'Interview',
  interview_held: 'Interviewed',
  candidate_accepted: 'Accepted',
  email_captured: 'Email',
  license_captured: 'License',
  background_check: 'Background',
  drug_screen: 'Drug test',
  mec: 'Medical',
  onboarding_packet: 'Onboarding',
  ssn_captured: 'SSN',
  uscis_captured: 'USCIS',
  everify: 'E-Verify',
  fedex_id: 'FedEx ID',
  safety_quiz: 'Quiz',
  btw_test: 'Road test',
  road_test: 'Road test',
  safety_certificate: 'Training',
  fa_uploads: 'FA upload',
  fa_compliant: 'Compliant',
  fedex_password: 'Password',
  sig: 'SIG',
  uniform_sizes: 'Uniform',
  intro_managers: 'Intro',
  beans_account: 'Beans',
  start_date: 'Start',
  fuel_card: 'Fuel card',
};
const STATUS_MIN_W = 160; // px — the Status column's floor; it grows to its longest status
const PROGRESS_W = 150; // px — the Progress column, also under the CANDIDATE band

const NOT_STARTED = '__not_started';
const IN_PROGRESS = '__in_progress';
const DONE = '__done';

/** Track order inside `clearing`, matching the checklist's columns. */
const TRACK_RANK: Record<string, number> = { vetting: 0, training: 1, paperwork: 2 };
// Trackless clearance steps (the First Advantage pair, where the three
// tracks meet) band AFTER the tracks, under Clearance's own name.
const TRACKLESS_RANK = 9;

/**
 * How a picker cell looks. Hoisted out of the row loop on purpose: an sx
 * object written inline is a new object on every render of every cell, and
 * at a thousand cells that is the frame budget.
 */
const PICKER_SX = {
  display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center',
  minWidth: 0, px: 0.75, py: 0.5, borderRadius: 1,
  fontSize: 12.5, cursor: 'pointer', userSelect: 'none',
  '&:hover': { bgcolor: 'action.hover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
} as const;

/** The Status cell's picker: words read from the left, caret at the far edge. */
const STATUS_PICKER_SX = { ...PICKER_SX, justifyContent: 'flex-start' } as const;

type SortDir = 'asc' | 'desc';
type SortKey = 'num' | 'name' | 'stage' | 'progress' | 'idle' | 'interview' | 'start' | { checkpoint: string };

/**
 * How far through their steps somebody is: done out of the steps that
 * apply to THEM. Every step in the catalog counts — conditional ones too,
 * when they apply — and a step marked not applicable (or one this person
 * has no cell for) is out of the total, not a zero. Part-way steps are
 * kept separately: they never inflate the fraction shown, they only break
 * ties when sorting, so of two people at 12/28 the one with more moving
 * comes first.
 */
interface Segment { label: string; stage: string; done: number; partial: number; total: number }
interface Progress {
  done: number; partial: number; total: number;
  /** One per STAGE — the bar. Pre-employment's four bands are one box. */
  segments: Segment[];
  /** One per band — the tooltip, where the detail has room. */
  bands: Segment[];
}

/**
 * Counted over the grid's own columns, band by band, so "14/19" is the
 * ticks you can see on the row, and the bar can show WHICH band the
 * missing five are in.
 */
function progressOf(row: Candidate, bands: Array<{ label: string; stage: string; keys: string[] }>): Progress {
  let done = 0;
  let partial = 0;
  let total = 0;
  const segments: Segment[] = [];
  const perBand: Segment[] = [];
  for (const band of bands) {
    const seg: Segment = { label: band.label, stage: band.stage, done: 0, partial: 0, total: 0 };
    for (const key of band.keys) {
      const cell = row.checkpoints?.[key];
      if (!cell || cell.status === 'not_applicable') continue;
      seg.total += 1;
      if (cell.status === 'done') seg.done += 1;
      else if (cell.status === 'in_progress') seg.partial += 1;
    }
    if (seg.total) {
      perBand.push(seg);
      const last = segments[segments.length - 1];
      if (last && last.stage === seg.stage) {
        last.done += seg.done; last.partial += seg.partial; last.total += seg.total;
      } else {
        segments.push({
          ...seg,
          label: STAGE_LABEL[seg.stage as CandidateStage] || seg.label,
        });
      }
    }
    done += seg.done;
    partial += seg.partial;
    total += seg.total;
  }
  return { done, partial, total, segments, bands: perBand };
}

/** The sortable score: done fraction, part-way steps as half. */
function progressScore(p: Progress): number {
  return p.total ? (p.done + p.partial * 0.5) / p.total : -1;
}

/**
 * Which drawer a booked interview belongs in.
 *
 * Coarser than the exact time on purpose: nobody filters a pipeline for
 * "14:30", they filter it for "who am I talking to today" and "who has
 * fallen through". Missed is its own bucket because it is the one that
 * needs doing something about.
 */
const INTERVIEW_BUCKETS: Array<{ id: string; label: string }> = [
  { id: 'missed', label: 'Missed' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Next 7 days' },
  { id: 'later', label: 'Later' },
  { id: 'held', label: 'Interviewed' },
  { id: 'none', label: 'Not scheduled' },
];

function interviewBucket(row: Candidate, now = new Date()): string {
  // An interview that HAPPENED is not a missed call, whatever the clock
  // says about the time it was booked for. This column stops being about
  // the future the moment the interview is done.
  if (row.checkpoints?.interview_held?.status === 'done') return 'held';
  if (!row.scheduledCallAt) return 'none';
  const at = new Date(row.scheduledCallAt);
  if (Number.isNaN(at.getTime())) return 'none';
  if (at.getTime() < now.getTime() - 2 * 60 * 1000) return 'missed';
  if (at.toDateString() === now.toDateString()) return 'today';
  return at.getTime() - now.getTime() <= 7 * 86400000 ? 'week' : 'later';
}

/**
 * A filterable bucket of a checkpoint column: 'done', 'not_started',
 * 'in_progress' (part-way with no named state), or 'state:<key>' for a
 * named state — so "only the ones whose link went out" is expressible.
 */
type Bucket = string;

function bucketOf(cell: Cell | null | undefined): Bucket | null {
  if (!cell || cell.status === 'not_applicable') return null;
  if (cell.status === 'done') return 'done';
  if (cell.status === 'in_progress') return cell.state ? `state:${cell.state}` : 'in_progress';
  return 'not_started';
}

/** How finished a cell is, for sorting a checkpoint column. */
function cellRank(cell: Cell | null | undefined): number {
  if (!cell || cell.status === 'not_applicable') return -1;
  if (cell.status === 'done') return 2;
  if (cell.status === 'in_progress') return 1;
  return 0;
}

/**
 * The status tint. Layered over the stripe as a gradient image, not as
 * the cell's bgcolor, so zebra and hover keep working underneath it —
 * and from `statusLight`, because alpha() throws on the CSS-variable
 * tokens.
 */
function tintFor(cell: Cell | null | undefined): string | undefined {
  if (!cell) return undefined;
  if (cell.status === 'done') return alpha(statusLight.ok, 0.14);
  if (cell.status === 'in_progress') return alpha(statusLight.warn, 0.16);
  return undefined;
}

const Glyph: React.FC<{ cell: Cell | null }> = ({ cell }) => {
  if (!cell || cell.status === 'not_applicable') {
    return <Typography variant="body2" color="text.disabled">–</Typography>;
  }
  if (cell.status === 'done') {
    return <DoneIcon sx={{ fontSize: 19, color: statusTokens.ok, display: 'block' }} />;
  }
  return (
    // 16 outside, 2 border, 6 dot: every gap divides evenly, so the dot
    // sits dead centre instead of a sub-pixel off it.
    <Box
      sx={{
        width: 16, height: 16, borderRadius: '3px',
        border: '2px solid', borderColor: cell.status === 'in_progress' ? statusTokens.warn : 'divider',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {cell.status === 'in_progress' && (
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusTokens.warn }} />
      )}
    </Box>
  );
};

/**
 * "14/28" over a thin bar, the percentage beside it. Memoised: it only
 * repaints when this one person's numbers change.
 */
const ProgressCell = React.memo<{
  value: Progress | undefined;
  stripe: string;
  cellSx: Record<string, unknown>;
  testId: string;
}>(({ value, stripe, cellSx, testId }) => {
  const total = value?.total ?? 0;
  const done = value?.done ?? 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const finished = total > 0 && done >= total;
  return (
    <Box
      component="td"
      data-testid={testId}
      sx={{
        ...cellSx, px: 1.25, bgcolor: stripe, minWidth: PROGRESS_W, maxWidth: PROGRESS_W,
      }}
    >
      {total === 0 ? (
        <Typography variant="body2" color="text.disabled" textAlign="center">–</Typography>
      ) : (
        <Tooltip
          placement="top"
          title={(
            <Box sx={{ py: 0.25 }}>
              {value!.bands.map((seg) => (
                <Stack key={seg.label} direction="row" gap={1.5} justifyContent="space-between">
                  <span>{seg.done >= seg.total ? '✓ ' : ''}{seg.label}</span>
                  <span>{seg.done}/{seg.total}{seg.partial ? ` · ${seg.partial} under way` : ''}</span>
                </Stack>
              ))}
            </Box>
          )}
        >
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.4 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {done}/{total}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {pct}%
              </Typography>
            </Stack>
            {/* One segment per band, as wide as its share of the steps,
                filled as far as that band is done, in the band's colour —
                so 14/19 also says WHERE the other five are. */}
            <Stack direction="row" gap="2px" sx={{ height: 6 }}>
              {value!.segments.map((seg) => {
                const colour = finished ? statusLight.ok : (STAGE_COLOR[seg.stage as CandidateStage] || statusLight.ok);
                const fill = Math.round((seg.done / seg.total) * 100);
                const part = Math.round((seg.partial / seg.total) * 100);
                return (
                  <Box
                    key={seg.label}
                    data-testid={`${testId}-${seg.label}`}
                    sx={{
                      flex: seg.total, minWidth: 3, borderRadius: '2px', overflow: 'hidden',
                      bgcolor: alpha(colour, 0.18), display: 'flex',
                    }}
                  >
                    <Box sx={{ width: `${fill}%`, bgcolor: colour }} />
                    {part > 0 && <Box sx={{ width: `${part}%`, bgcolor: alpha(colour, 0.5) }} />}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
});
ProgressCell.displayName = 'ProgressCell';

const CandidateGrid: React.FC<{
  rows: Candidate[];
  catalog: Checkpoint[];
  stalledIds: Set<string>;
  onOpen: (row: Candidate) => void;
  onEditField: (row: Candidate, patch: Record<string, any>) => void;
  onSetStage: (row: Candidate, stage: CandidateStage) => void;
  onClose: (row: Candidate, outcome: CandidateOutcome) => void;
  onDelete: (rows: Candidate[]) => void;
  onSetCheckpoint: (
    row: Candidate,
    key: string,
    body: { status: CheckpointStatus; state?: string | null; value?: string },
  ) => void;
  onUpload: (row: Candidate, checkpoint: Checkpoint) => void;
  /** Copy the message that fits this candidate, without leaving the list. */
  onCopyMessage: (row: Candidate) => void;
  /** Copy their number, for the call that goes with it. */
  onCopyPhone: (row: Candidate) => void;
  /** Mark somebody worth watching, or stop. */
  onStar: (row: Candidate, starred: boolean) => void;
  /** Show only the starred ones. Owned above, because the to-do list
   *  offers the same filter and the two share the word. */
  starredOnly?: boolean;
  onStarredOnly?: (next: boolean) => void;
}> = ({
  rows, catalog, stalledIds, onOpen, onEditField, onSetStage, onClose, onDelete,
  onSetCheckpoint, onUpload, onCopyMessage, onCopyPhone, onStar,
  starredOnly = false, onStarredOnly,
}) => {
  // Popover state for value-backed cells.
  const [valueFor, setValueFor] = useState<{
    row: Candidate; checkpoint: Checkpoint; anchor: HTMLElement;
  } | null>(null);
  const [draft, setDraft] = useState('');

  // Sorting, per-column status filters, the ticked rows, bulk menus.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);
  const [filters, setFilters] = useState<Record<string, Set<Bucket>>>({});
  const [menuFor, setMenuFor] = useState<{ checkpoint: Checkpoint; anchor: HTMLElement } | null>(null);
  /** The interview column's own filter — buckets of time, not states. */
  const [interviewFilter, setInterviewFilter] = useState<Set<string>>(new Set());
  const [interviewMenu, setInterviewMenu] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [closeMenuAnchor, setCloseMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Picking Hired asks the one question that matters — invite them too?
  // — and takes no for an answer.
  const [hireAsk, setHireAsk] = useState<Candidate | null>(null);
  /**
   * ONE menu for every state cell, and one for every Status cell, opened
   * against whichever was clicked. Forty rows used to mount a MUI Select
   * per multi-state column — around three hundred of them before a single
   * row could be read. A cell is a span now; this is the control.
   */
  const [stateFor, setStateFor] = useState<{
    row: Candidate; checkpoint: Checkpoint; current: string; anchor: HTMLElement;
  } | null>(null);
  const [stageFor, setStageFor] = useState<{ row: Candidate; anchor: HTMLElement } | null>(null);
  // The grid's own search and stage filter — they used to live in the
  // page header, a different control style for the same job the column
  // menus already do.
  const [nameQuery, setNameQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<Set<CandidateStage>>(new Set());
  const [nameMenuAnchor, setNameMenuAnchor] = useState<HTMLElement | null>(null);
  const [stageMenuAnchor, setStageMenuAnchor] = useState<HTMLElement | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  // Whether the steps have scrolled: the CANDIDATE band centres across
  // all three of its columns until Status slides away, then across the
  // frozen pair only.
  const [scrolledX, setScrolledX] = useState(false);

  /** Page the grid sideways — half a screen per press, smoothly. */
  const swipe = (direction: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * 0.5, behavior: 'smooth' });
  };

  const ordered = useMemo(() => {
    const stageRank = new Map(STAGE_ORDER.map((s, i) => [s, i]));
    return [...catalog]
      // A conditional step gets no column — it lives on the person's page.
      .filter((c) => !(c as any).onlyWhen)
      .sort((a, b) =>
        ((stageRank.get(a.stage as any) ?? 99) - (stageRank.get(b.stage as any) ?? 99))
        || ((a.track ? TRACK_RANK[a.track] ?? 0 : TRACKLESS_RANK) - (b.track ? TRACK_RANK[b.track] ?? 0 : TRACKLESS_RANK))
        || ((a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
  }, [catalog]);

  /**
   * The bands across the top, each carrying its stage so the header can
   * wear the stage's colour. A band is a stage — except Clearance, whose
   * three tracks band separately but all wear Clearance's violet.
   */
  const bands = useMemo(() => {
    const list: Array<{ label: string; count: number; stage: string; keys: string[] }> = [];
    const starts = new Set<string>();
    let previous = '';
    for (const c of ordered) {
      const label = (c.stage === 'clearing' && c.track && TRACK_LABEL[c.track])
        ? TRACK_LABEL[c.track]
        : (STAGE_LABEL[c.stage as keyof typeof STAGE_LABEL] || String(c.stage));
      if (label === previous) { list[list.length - 1].count += 1; list[list.length - 1].keys.push(c.key); }
      else { list.push({ label, count: 1, stage: String(c.stage), keys: [c.key] }); starts.add(c.key); previous = label; }
    }
    return { list, starts };
  }, [ordered]);

  const isWide = (c: Checkpoint) => !!(c.states && c.states.length);

  /**
   * The interview's own column, which is a date rather than a state.
   *
   * It sits in the process — between the reply and the interview itself —
   * because that is where somebody reading across a candidate expects to
   * find the time they agreed to. It used to live at the far right with
   * the notes, which put the most perishable fact in the pipeline behind
   * twenty columns of scrolling.
   */
  const isInterviewDate = (c: Checkpoint) =>
    c.valueType === 'datetime' && c.candidateField === 'scheduledCallAt';

  /** The name column fits the longest name in the pipeline — no cuts. */
  const nameWidth = useMemo(() => {
    const longest = rows.reduce((max, r) => Math.max(max, candidateName(r).length), 10);
    // ~7.5px per character at body2/700, plus padding.
    return Math.min(360, Math.max(170, Math.round(longest * 7.5) + 28));
  }, [rows]);

  /** Each row's progress, worked out once per data change, not per render. */
  const progress = useMemo(() => {
    const map = new Map<string, Progress>();
    for (const row of rows) map.set(row._id, progressOf(row, bands.list));
    return map;
  }, [rows, bands]);

  /** The step each person is actually on — what the Status column says. */
  const statuses = useMemo(() => {
    const map = new Map<string, { label: string; key: string | null }>();
    for (const row of rows) map.set(row._id, stepStatus(row, ordered));
    return map;
  }, [rows, ordered]);

  /**
   * The Status column fits its longest status — measured, not guessed
   * from a character count, because "Background check pending" and
   * "Compliant" differ by more than their lengths. A status is never cut.
   */
  const statusWidth = useMemo(() => {
    let longest = 0;
    const ctx = typeof document !== 'undefined'
      ? document.createElement('canvas').getContext?.('2d') : null;
    if (ctx) ctx.font = '12.5px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    statuses.forEach(({ label }) => {
      const w = ctx ? ctx.measureText(label).width : label.length * 7.2;
      if (w > longest) longest = w;
    });
    // dot 8 + two gaps 8 + caret 16 + picker padding 12 + cell padding 8 + slack 12
    return Math.max(STATUS_MIN_W, Math.ceil(longest) + 64);
  }, [statuses]);

  /** Filters first, then the sort; the props stay the source of truth. */
  const visible = useMemo(() => {
    let out = rows;

    const active = Object.entries(filters).filter(([, set]) => set.size > 0);
    if (active.length) {
      out = out.filter((row) => active.every(([key, want]) => {
        const bucket = bucketOf(row.checkpoints?.[key]);
        return bucket != null && want.has(bucket);
      }));
    }

    if (nameQuery.trim()) {
      const q = nameQuery.trim().toLowerCase();
      out = out.filter((row) => candidateName(row).toLowerCase().includes(q));
    }
    if (stageFilter.size) {
      out = out.filter((row) => stageFilter.has(row.stage));
    }
    if (starredOnly) {
      out = out.filter((row) => !!row.starred);
    }
    if (interviewFilter.size) {
      out = out.filter((row) => interviewFilter.has(interviewBucket(row)));
    }

    // No sort chosen means the ledger's own order: by row number. That is
    // what a numbered first column promises.
    if (!sort) {
      return [...out].sort((a, b) => (a.num ?? Infinity) - (b.num ?? Infinity));
    }
    {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const stageRank = new Map(STAGE_ORDER.map((s, i) => [s, i]));
      out = [...out].sort((a, b) => {
        if (sort.key === 'num') return (((a.num ?? 0) - (b.num ?? 0))) * dir;
        if (sort.key === 'name') return candidateName(a).localeCompare(candidateName(b)) * dir;
        if (sort.key === 'stage') {
          // Within a stage, the further along first (or last), so the
          // sort reads as a queue rather than as a shuffle.
          const pa = progress.get(a._id);
          const pb = progress.get(b._id);
          return (((stageRank.get(a.stage) ?? 99) - (stageRank.get(b.stage) ?? 99))
            || ((pa ? progressScore(pa) : -1) - (pb ? progressScore(pb) : -1))) * dir;
        }
        if (sort.key === 'progress') {
          const pa = progress.get(a._id);
          const pb = progress.get(b._id);
          const sa = pa ? progressScore(pa) : -1;
          const sb = pb ? progressScore(pb) : -1;
          // Nobody with no steps at all floats into the middle: last, both ways.
          if (sa < 0 && sb < 0) return 0;
          if (sa < 0) return 1;
          if (sb < 0) return -1;
          return (sa - sb) * dir || ((a.num ?? 0) - (b.num ?? 0));
        }
        if (sort.key === 'idle') {
          return ((a.daysSinceActivity ?? -1) - (b.daysSinceActivity ?? -1)) * dir;
        }
        if (sort.key === 'start') {
          // Like the interview: no start date is absent, not early or late.
          const at = (row: Candidate) => (row.targetStartDate ? new Date(row.targetStartDate).getTime() : null);
          const av = at(a);
          const bv = at(b);
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * dir;
        }
        if (sort.key === 'interview') {
          // Soonest first one way, latest first the other — and nobody
          // without a booked interview floats into the middle of either:
          // they sit at the end of both, because "no time" is not early
          // and it is not late, it is absent.
          const at = (row: Candidate) => (row.scheduledCallAt
            ? new Date(row.scheduledCallAt).getTime() : null);
          const av = at(a);
          const bv = at(b);
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * dir;
        }
        const key = (sort.key as { checkpoint: string }).checkpoint;
        // The interview column holds a moment in time, so it sorts like
        // one: soonest first, and nobody without a booked interview floats
        // into the middle — "no time" is not early and it is not late.
        if (catalog.some((c) => c.key === key && isInterviewDate(c))) {
          const when = (row: Candidate) => (row.scheduledCallAt
            ? new Date(row.scheduledCallAt).getTime() : null);
          const av = when(a);
          const bv = when(b);
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * dir;
        }
        return (cellRank(a.checkpoints?.[key]) - cellRank(b.checkpoints?.[key])) * dir;
      });
    }
    return out;
  }, [rows, filters, sort, nameQuery, stageFilter, interviewFilter, starredOnly, catalog, progress]);

  /** Progress starts with the furthest along: most done → least done → original. */
  const cycleProgressSort = () => {
    setSort((current) => {
      if (!current || current.key !== 'progress') return { key: 'progress', dir: 'desc' };
      if (current.dir === 'desc') return { key: 'progress', dir: 'asc' };
      return null;
    });
  };

  const cycleSort = (key: 'num' | 'name' | 'stage' | 'idle' | 'interview' | 'start') => {
    setSort((current) => {
      if (!current || current.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  /**
   * Same rhythm everywhere: done first, not done first, original.
   *
   * A DATE column starts the other way round, because the useful first
   * answer there is "who am I talking to soonest" rather than "who is
   * furthest off".
   */
  const cycleCheckpointSort = (key: string, firstDir: SortDir = 'desc') => {
    setSort((current) => {
      const mine = current && typeof current.key === 'object' && current.key.checkpoint === key;
      if (!mine) return { key: { checkpoint: key }, dir: firstDir };
      if (current!.dir === firstDir) {
        return { key: { checkpoint: key }, dir: firstDir === 'desc' ? 'asc' : 'desc' };
      }
      return null;
    });
  };

  const arrowFor = (key: 'num' | 'name' | 'stage' | 'progress' | 'idle' | 'interview' | 'start') =>
    (sort && sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');

  /**
   * One sort control, worded for the data it sorts. "✓ first / ◻ first"
   * as two buttons was rightly called dumb: the button shows where the
   * order stands and a click advances it — Original → one way → the
   * other → back.
   */
  const sortButton = (
    key: SortKey,
    labels: { desc: string; asc: string },
    firstDir: SortDir = 'desc',
  ) => {
    const isMine = sort && (typeof key === 'object'
      ? typeof sort.key === 'object' && sort.key.checkpoint === key.checkpoint
      : sort.key === key);
    const label = !isMine ? 'Original order' : (sort!.dir === 'desc' ? labels.desc : labels.asc);
    const advance = () => setSort(() => {
      if (!isMine) return { key, dir: firstDir };
      if (sort!.dir === firstDir) return { key, dir: firstDir === 'desc' ? 'asc' : 'desc' };
      return null;
    });
    return (
      <Button size="small" variant="outlined" color="inherit" fullWidth onClick={advance}>
        {label}
      </Button>
    );
  };

  const toggleRow = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((current) => (current.size === visible.length
      ? new Set<string>()
      : new Set(visible.map((r) => r._id))));
  };

  const selectedRows = useMemo(
    () => visible.filter((r) => selected.has(r._id)),
    [visible, selected],
  );

  /**
   * Who a checkpoint edit lands on. Tick rows and the edit fans out to
   * everyone ticked — but only for manual toggles and state picks. A
   * document is one person's file and a value is one person's number, so
   * those stay strictly on the row that was clicked.
   */
  const targetsFor = (row: Candidate): Candidate[] => {
    if (!selected.size || !selected.has(row._id)) return [row];
    return visible.filter((r) => selected.has(r._id));
  };

  const openValue = (row: Candidate, checkpoint: Checkpoint, anchor: HTMLElement) => {
    setDraft(row.checkpoints?.[checkpoint.key]?.value || '');
    setValueFor({ row, checkpoint, anchor });
  };

  const clickGlyph = (row: Candidate, checkpoint: Checkpoint, target: HTMLElement) => {
    const cell = row.checkpoints?.[checkpoint.key];
    if (!cell) return; // not applicable to this person
    if (checkpoint.kind === 'field') return; // reflects the record; edited there

    if (checkpoint.kind === 'document') {
      if (cell.status !== 'done') onUpload(row, checkpoint);
      return; // a filed document is un-filed on their page, deliberately
    }
    if (checkpoint.kind === 'value') {
      openValue(row, checkpoint, target);
      return;
    }
    const body = cell.status === 'done'
      ? { status: 'not_started' as CheckpointStatus, state: null }
      : { status: 'done' as CheckpointStatus };
    targetsFor(row).forEach((t) => onSetCheckpoint(t, checkpoint.key, body));
  };

  const pickState = (row: Candidate, checkpoint: Checkpoint, picked: string, target: HTMLElement) => {
    if (picked === NOT_STARTED) {
      targetsFor(row).forEach((t) => onSetCheckpoint(t, checkpoint.key, { status: 'not_started', state: null }));
      return;
    }
    if (picked === DONE) {
      if (checkpoint.kind === 'document') { onUpload(row, checkpoint); return; }
      // An optional value never stands between the click and Done — the
      // safety quiz's date sharpens the road-test countdown when it is
      // known, and is skipped when it is not.
      if (checkpoint.kind === 'value' && !checkpoint.valueOptional) { openValue(row, checkpoint, target); return; }
      targetsFor(row).forEach((t) => onSetCheckpoint(t, checkpoint.key, { status: 'done' }));
      return;
    }
    targetsFor(row).forEach((t) => onSetCheckpoint(t, checkpoint.key, { status: 'in_progress', state: picked }));
  };

  /**
   * A tooltip only where the cell's behaviour is not obvious. A plain
   * tick explaining "click when it is done" is a checkbox describing
   * itself; a document cell, a value, a field mirror and a does-not-apply
   * dash genuinely carry rules a glyph cannot say.
   */
  const tipFor = (row: Candidate, checkpoint: Checkpoint): string | null => {
    const cell = row.checkpoints?.[checkpoint.key];
    const label = checkpointLabel(checkpoint);
    if (!cell) return `${label} — does not apply to ${candidateName(row)}`;
    if (checkpoint.kind === 'field') {
      return `${label} — filled from the record${cell.status === 'done' ? '' : ', still empty'}`;
    }
    if (checkpoint.kind === 'document') {
      return cell.status === 'done'
        ? `${label} — file on record. Manage it on their page.`
        : `${label} — click to file the document`;
    }
    if (checkpoint.kind === 'value') {
      return cell.status === 'done' && cell.value ? `${label}: ${cell.value}` : `${label} — click to enter it`;
    }
    return null;
  };

  const rule = (key: string) => ({
    borderRight: '1px solid', borderRightColor: 'grey.100',
    ...(bands.starts.has(key)
      ? { borderLeft: '1px solid', borderLeftColor: 'divider' }
      : {}),
  });

  const headCell = {
    p: 0, height: BAND_H, textAlign: 'center' as const,
    borderBottom: '1px solid', borderColor: 'divider',
    bgcolor: HEAD_BG, position: 'sticky' as const, top: 0, zIndex: 2,
  };
  const bodyCell = {
    height: ROW,
    borderBottom: '1px solid', borderColor: 'divider',
  };

  return (
    <>
      {/* The colour code, named — and the same colours the bands wear. */}
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 1, px: 0.5 }}>
        {STAGE_ORDER.map((stage) => (
          <Stack key={stage} direction="row" alignItems="center" gap={0.75}>
            <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: STAGE_COLOR[stage] }} />
            <Typography variant="caption" color="text.secondary">
              {STAGE_LABEL[stage]}
            </Typography>
          </Stack>
        ))}
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: statusTokens.warn }} />
          {/* Not "gone quiet". The amber stripe covers two different
              things — somebody who has stopped replying, and somebody
              nobody has written to yet — and only one of those is their
              doing. The stripe means the same either way: this one has
              been sitting too long. */}
          <Typography variant="caption" color="text.secondary">Waiting too long</Typography>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 'auto' }}>
          {selected.size > 0 && (
            <>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${selected.size} selected — edits apply to all`}
              />
              <Button size="small" variant="outlined" color="inherit" onClick={(e) => setCloseMenuAnchor(e.currentTarget)}>
                Close as…
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setConfirmDelete(true)}>
                Delete…
              </Button>
              <Button size="small" color="inherit" onClick={() => setSelected(new Set())}>Clear</Button>
            </>
          )}
          <Stack direction="row" gap={0.25}>
            <IconButton size="small" onClick={() => swipe(-1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <LeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => swipe(1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <RightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>

    <Box
      ref={scroller}
      onScroll={(e: React.UIEvent<HTMLDivElement>) => {
        const gone = (e.target as HTMLDivElement).scrollLeft > 8;
        setScrolledX((current) => (current === gone ? current : gone));
      }}
      sx={{
        // The table scrolls INSIDE itself on both axes — the page stays
        // put, so the headers are always on screen without the reader
        // paying attention to how.
        overflow: 'auto',
        maxHeight: 'calc(100vh - 250px)',
        border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper',
      }}
    >
      <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
        <Box component="thead">
          {/* Row 1 — the bands, wearing their stage's colour. */}
          <Box component="tr">
            <Box component="th" colSpan={2} sx={{ ...headCell, position: 'sticky', left: 0, zIndex: 3, bgcolor: HEAD_BG, ...(scrolledX ? { borderRight: '1px solid', borderRightColor: 'divider' } : {}) }}>
              {/* Centred over all three of its columns — #, Name, Status —
                  until Status scrolls away under it, then over the frozen
                  pair. The cell itself can only span what freezes. */}
              <Box
                sx={{
                  position: 'absolute', inset: 0,
                  width: scrolledX ? '100%' : `calc(100% + ${statusWidth + PROGRESS_W}px)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'width 150ms',
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="caption" color="text.secondary" noWrap sx={HEAD_TEXT}>
                  Candidate
                </Typography>
              </Box>
            </Box>
            {/* Status and Progress share the group visually; their band
                cells stay unlabelled because they cannot freeze with the pair. */}
            <Box component="th" sx={{ ...headCell, bgcolor: HEAD_BG }} />
            <Box component="th" sx={{ ...headCell, bgcolor: HEAD_BG, borderRight: '1px solid', borderRightColor: 'divider' }} />
            {bands.list.map((band) => {
              const colour = STAGE_COLOR[band.stage as CandidateStage];
              return (
                <Box
                  key={band.label}
                  component="th"
                  colSpan={band.count}
                  sx={{
                    ...headCell, px: 1,
                    borderLeft: '1px solid', borderLeftColor: 'divider',
                    // Tint LAYERED on an opaque ground: an alpha bgcolor on
                    // a sticky header is a window onto whatever scrolled
                    // beneath it — the body's ticks showed through the
                    // INTERVIEW band.
                    bgcolor: 'background.paper',
                    backgroundImage: colour
                      ? `linear-gradient(${alpha(colour, 0.09)}, ${alpha(colour, 0.09)})`
                      : undefined,
                    ...(colour ? {} : { bgcolor: HEAD_BG }),
                  }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      ...HEAD_TEXT,
                      color: colour || HEAD_TEXT.color,
                    }}
                  >
                    {band.label}
                  </Typography>
                </Box>
              );
            })}
            <Box component="th" colSpan={3} sx={{ ...headCell, bgcolor: HEAD_BG }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={HEAD_TEXT}>
                Info
              </Typography>
            </Box>
          </Box>
          {/* Row 2 — the columns, wearing their real names, centred. */}
          <Box component="tr" className="grid-head">
            <Box
              component="th"
              sx={{
                ...headCell, top: BAND_H, minWidth: NUM_W, maxWidth: NUM_W,
                position: 'sticky', left: 0, zIndex: 3, cursor: 'pointer',
              }}
            >
              <Box sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography
                  variant="caption" color="text.secondary"
                  onClick={() => cycleSort('num')}
                  sx={{
                    ...HEAD_TEXT,
                    opacity: selected.size > 0 ? 0 : 1,
                    '.grid-head:hover &': { opacity: 0 },
                  }}
                >
                  #{arrowFor('num')}
                </Typography>
                <Checkbox
                  size="small"
                  sx={{
                    p: 0.25, position: 'absolute',
                    opacity: selected.size > 0 ? 1 : 0,
                    '.grid-head:hover &': { opacity: 1 },
                  }}
                  checked={visible.length > 0 && selected.size === visible.length}
                  indeterminate={selected.size > 0 && selected.size < visible.length}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'Select everyone' }}
                />
              </Box>
            </Box>
            <Box
              component="th"
              sx={{
                ...headCell, top: BAND_H, textAlign: 'left', px: 1.25, minWidth: nameWidth,
                position: 'sticky', left: NUM_W, zIndex: 3, cursor: 'pointer',
                // The group's boundary lives after STATUS. Only when
                // Status scrolls under the frozen pair does the edge
                // line move here — inside the group, no line at all.
                ...(scrolledX ? { borderRight: '1px solid', borderRightColor: 'divider' } : {}),
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.25}>
                <Typography
                  variant="caption"
                  sx={{ ...HEAD_TEXT, color: nameQuery ? 'primary.main' : HEAD_TEXT.color }}
                  onClick={() => cycleSort('name')}
                >
                  Name{arrowFor('name')}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setNameMenuAnchor(e.currentTarget); }}
                  sx={{ p: 0, color: nameQuery ? 'primary.main' : 'text.disabled' }}
                >
                  <CaretIcon sx={{ fontSize: 16 }} />
                </IconButton>
                {/* The starred filter lives in the column the stars are
                    in, like every other filter on this grid. */}
                {onStarredOnly && (
                  <Tooltip title={starredOnly ? 'Show everybody' : 'Only the ones worth watching'}>
                    <IconButton
                      size="small"
                      data-testid="filter-starred"
                      onClick={(e) => { e.stopPropagation(); onStarredOnly(!starredOnly); }}
                      sx={{ p: 0, ml: 0.25, color: starredOnly ? statusTokens.warn : 'text.disabled' }}
                    >
                      {starredOnly
                        ? <StarIcon sx={{ fontSize: 15 }} />
                        : <StarBorderIcon sx={{ fontSize: 15 }} />}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
            <Box
              component="th"
              data-testid="progress-head"
              onClick={cycleProgressSort}
              sx={{
                ...headCell, top: BAND_H, minWidth: PROGRESS_W, maxWidth: PROGRESS_W, cursor: 'pointer',
              }}
            >
              <Tooltip title="Steps done, out of the ones that apply to them. Click to sort." placement="top">
                <Typography
                  variant="caption"
                  sx={{ ...HEAD_TEXT, color: sort?.key === 'progress' ? 'primary.main' : HEAD_TEXT.color }}
                >
                  Progress{arrowFor('progress')}
                </Typography>
              </Tooltip>
            </Box>
            <Box
              component="th"
              onClick={() => cycleSort('stage')}
              sx={{ ...headCell, top: BAND_H, minWidth: statusWidth, width: statusWidth, cursor: 'pointer', borderRight: '1px solid', borderRightColor: 'divider' }}
            >
              <Stack direction="row" alignItems="center" justifyContent="center" gap={0.25}>
                <Typography
                  variant="caption"
                  sx={{ ...HEAD_TEXT, color: stageFilter.size ? 'primary.main' : HEAD_TEXT.color }}
                >
                  Status{arrowFor('stage')}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setStageMenuAnchor(e.currentTarget); }}
                  sx={{ p: 0, color: stageFilter.size ? 'primary.main' : 'text.disabled' }}
                >
                  <CaretIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Box>
            {ordered.map((c) => {
              const dated = isInterviewDate(c);
              const filtered = dated ? interviewFilter.size > 0 : !!filters[c.key];
              const sorted = !!sort && typeof sort.key === 'object' && sort.key.checkpoint === c.key;
              return (
                <Box
                  key={c.key}
                  component="th"
                  data-testid={`head-${c.key}`}
                  onClick={() => cycleCheckpointSort(c.key, dated ? 'asc' : 'desc')}
                  onContextMenu={(e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    if (dated) setInterviewMenu(e.currentTarget);
                    else setMenuFor({ checkpoint: c, anchor: e.currentTarget });
                  }}
                  sx={{
                    ...headCell, top: BAND_H, cursor: 'pointer', ...rule(c.key),
                    height: HEAD_ROW_HEIGHT, px: 1, verticalAlign: 'middle',
                    minWidth: isWide(c) || dated ? WIDE : CELL,
                    maxWidth: isWide(c) || dated ? WIDE : CELL,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="center" gap={0}>
                    <Tooltip title={checkpointLabel(c)} placement="top">
                      <Typography
                        variant="caption"
                        sx={{
                          ...HEAD_TEXT, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
                          color: filtered || sorted ? 'primary.main' : HEAD_TEXT.color,
                        }}
                      >
                        {SHORT_LABEL[c.key] || checkpointLabel(c)}
                        {sorted ? (sort!.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                      </Typography>
                    </Tooltip>
                    <IconButton
                      size="small"
                      data-testid={`filter-${c.key}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (dated) setInterviewMenu(e.currentTarget);
                        else setMenuFor({ checkpoint: c, anchor: e.currentTarget });
                      }}
                      sx={{ p: 0, ml: 0.25, color: filtered ? 'primary.main' : 'text.disabled' }}
                    >
                      <CaretIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
            <Box
              component="th"
              onClick={() => cycleSort('start')}
              sx={{ ...headCell, top: BAND_H, height: HEAD_ROW_HEIGHT, minWidth: 120, px: 1.25, cursor: 'pointer' }}
            >
              <Typography variant="caption" sx={HEAD_TEXT}>
                Start date{arrowFor('start')}
              </Typography>
            </Box>
            <Box component="th" sx={{ ...headCell, top: BAND_H, height: HEAD_ROW_HEIGHT, minWidth: 220, px: 1.25 }}>
              <Typography variant="caption" sx={HEAD_TEXT}>Notes</Typography>
            </Box>
            <Box
              component="th"
              onClick={() => cycleSort('idle')}
              sx={{ ...headCell, top: BAND_H, height: HEAD_ROW_HEIGHT, minWidth: 100, px: 1.25, cursor: 'pointer' }}
            >
              {/* "Idle" had to be asked about. This is just what it is. */}
              <Typography variant="caption" sx={HEAD_TEXT}>
                Last touched{arrowFor('idle')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box component="tbody">
          {visible.map((row, index) => {
            const stalled = stalledIds.has(row._id);
            const ticked = selected.has(row._id);
            // Solid stripes, never alpha: the frozen name cell inherits
            // the same colour, and a translucent one lets the scrolled
            // columns show through it.
            const stripe = index % 2 ? 'grey.50' : 'background.paper';
            return (
              <Box
                component="tr"
                key={row._id}
                className="grid-row"
                sx={{
                  '&:hover td': { bgcolor: 'grey.100' },
                  // The message button appears on the row you are pointing
                  // at. A hundred of them down a frozen column is a column
                  // of icons, not a list of names.
                  '&:hover .row-message': { opacity: 1 },
                }}
              >
                <Box
                  component="td"
                  sx={{
                    ...bodyCell,
                    position: 'sticky', left: 0, zIndex: 2,
                    bgcolor: ticked ? 'grey.100' : stripe,
                    minWidth: NUM_W, maxWidth: NUM_W, textAlign: 'center',
                    // The strip lives on the ledger's edge now that the
                    // number is the first column: the stage's colour —
                    // except gone-quiet, where amber wins. Both are in
                    // the legend.
                    boxShadow: `inset 5px 0 0 ${stalled ? statusTokens.warn : (STAGE_COLOR[row.stage] || 'transparent')}`,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  {/* The number IS the row's handle: hover (or an active
                      selection) turns it into its checkbox, the way a
                      mail client does it, so selecting costs no column. */}
                  {/* pl matches the 5px stage strip, or every number
                      reads a hair off-centre. */}
                  <Box sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pl: '5px' }}>
                    <Typography
                      variant="caption" color="text.secondary"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        opacity: ticked || selected.size > 0 ? 0 : 1,
                        '.grid-row:hover &': { opacity: 0 },
                      }}
                    >
                      {row.num ?? '—'}
                    </Typography>
                    <Checkbox
                      size="small"
                      sx={{
                        p: 0.25, position: 'absolute',
                        opacity: ticked || selected.size > 0 ? 1 : 0,
                        '.grid-row:hover &': { opacity: 1 },
                      }}
                      checked={ticked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleRow(row._id)}
                      inputProps={{ 'aria-label': `Select ${candidateName(row)}` }}
                    />
                  </Box>
                </Box>
                <Box
                  component="td"
                  sx={{
                    ...bodyCell,
                    position: 'sticky', left: NUM_W, zIndex: 2,
                    bgcolor: ticked ? 'grey.100' : stripe,
                    px: 0.75, cursor: 'pointer',
                    ...(scrolledX ? { borderRight: '1px solid', borderRightColor: 'divider' } : {}),
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                    {/* The one judgement the pipeline cannot make. A
                        star is always visible once set and appears on
                        hover when it is not, so forty rows of empty
                        outlines do not compete with the names. */}
                    <Tooltip title={row.starred ? 'Stop watching' : 'Worth watching'}>
                      <IconButton
                        size="small"
                        className={row.starred ? undefined : 'row-message'}
                        data-testid={`star-${row._id}`}
                        onClick={(e) => { e.stopPropagation(); onStar(row, !row.starred); }}
                        sx={{
                          p: 0.25,
                          flexShrink: 0,
                          color: row.starred ? statusTokens.warn : 'text.disabled',
                          ...(row.starred ? {} : { opacity: 0, transition: 'opacity 120ms' }),
                          '&:focus-visible': { opacity: 1 },
                        }}
                      >
                        {row.starred
                          ? <StarIcon sx={{ fontSize: 16 }} />
                          : <StarBorderIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="body2" fontWeight={700} noWrap
                      onClick={() => onOpen(row)}
                      sx={{ minWidth: 0, pl: 0.25 }}
                    >
                      {candidateName(row)}
                    </Typography>
                    {/* The message, from the list. Most of the work in the
                        first four stages IS the next text; opening the
                        candidate to send it is three clicks for something
                        the row already knows the answer to. It lives in
                        the frozen name column so it is reachable however
                        far the checkpoints are scrolled, and it shows on
                        hover — the same rule the row number's checkbox
                        follows, for the same reason. */}
                    {/* The number, one click. Reaching somebody is a
                        phone and a message, and both were a page away
                        from the row that says who to reach. */}
                    {row.phone && (
                      <Tooltip title={`Copy ${row.phone}`}>
                        <IconButton
                          size="small"
                          className="row-message"
                          data-testid={`copy-phone-${row._id}`}
                          onClick={(e) => { e.stopPropagation(); onCopyPhone(row); }}
                          sx={{
                            p: 0.25, flexShrink: 0, color: 'text.disabled',
                            opacity: 0,
                            transition: 'opacity 120ms',
                            '&:hover': { color: 'primary.main' },
                            '&:focus-visible': { opacity: 1 },
                          }}
                        >
                          <PhoneIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Copy the message that fits">
                      <IconButton
                        size="small"
                        className="row-message"
                        data-testid={`copy-message-${row._id}`}
                        onClick={(e) => { e.stopPropagation(); onCopyMessage(row); }}
                        sx={{
                          p: 0.25, flexShrink: 0, color: 'text.disabled',
                          opacity: 0,
                          transition: 'opacity 120ms',
                          '&:hover': { color: 'primary.main' },
                          '&:focus-visible': { opacity: 1 },
                        }}
                      >
                        <MessageIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <ProgressCell value={progress.get(row._id)} stripe={stripe} cellSx={bodyCell} testId={`progress-${row._id}`} />
                <Box component="td" data-testid={`status-${row._id}`} sx={{ ...bodyCell, px: 0.5, bgcolor: stripe, textAlign: 'left', minWidth: statusWidth, borderRight: '1px solid', borderRightColor: 'divider' }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={(e: React.MouseEvent<HTMLElement>) => setStageFor({ row, anchor: e.currentTarget })}
                    onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setStageFor({ row, anchor: e.currentTarget });
                      }
                    }}
                    sx={STATUS_PICKER_SX}
                  >
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: STAGE_COLOR[row.stage] || 'transparent', display: 'inline-block' }} />
                    <Tooltip title={`Stage: ${STAGE_LABEL[row.stage] || row.stage}`} placement="top">
                      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                        {statuses.get(row._id)?.label || STAGE_LABEL[row.stage] || row.stage}
                      </Box>
                    </Tooltip>
                    <CaretIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0, ml: 'auto' }} />
                  </Box>
                </Box>

                {ordered.map((c) => {
                  const cell = row.checkpoints?.[c.key] || null;
                  // Status as a wash the stripe shows through: green is
                  // done, amber is part-way. Layered as an image so
                  // zebra and hover keep working underneath.
                  const tint = tintFor(cell);
                  const layer = tint ? { backgroundImage: `linear-gradient(${tint}, ${tint})` } : {};

                  // A date, not a tick. Same control as everywhere else,
                  // so booking a call never means opening the candidate.
                  if (isInterviewDate(c)) {
                    // Once the step this column retires is done, the
                    // column stops being a countdown and becomes a
                    // record: WHEN the interview happened, in plain grey.
                    // Leaving it red and shouting "Missed · 48 min ago"
                    // beside a green "Interviewed" was the screen
                    // contradicting itself in one glance.
                    const retiredBy = c.untilDone ? row.checkpoints?.[c.untilDone] : undefined;
                    if (retiredBy?.status === 'done') {
                      return (
                        <Box
                          key={c.key}
                          component="td"
                          data-testid={`grid-${row._id}-${c.key}`}
                          sx={{
                            ...bodyCell, ...rule(c.key), px: 0.5, bgcolor: stripe,
                            textAlign: 'center', minWidth: WIDE, maxWidth: WIDE,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {retiredBy.completedAt
                              ? new Date(retiredBy.completedAt).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric',
                              })
                              : '—'}
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Box
                        key={c.key}
                        component="td"
                        data-testid={`grid-${row._id}-${c.key}`}
                        sx={{
                          ...bodyCell, ...rule(c.key), px: 0.5, bgcolor: stripe,
                          textAlign: 'center', minWidth: WIDE, maxWidth: WIDE,
                        }}
                      >
                        <ScheduleCall
                          variant="text"
                          scheduledCallAt={row.scheduledCallAt}
                          onSave={(iso) => onEditField(row, { scheduledCallAt: iso })}
                        />
                      </Box>
                    );
                  }

                  if (isWide(c)) {
                    // A part-way cell with no NAMED state (an import's
                    // "Processing") must not fall back to "Not started":
                    // the amber tint told the truth and the words
                    // contradicted it, in the same cell.
                    const current = !cell ? NOT_STARTED
                      : cell.status === 'done' ? DONE
                        : cell.status === 'in_progress' ? (cell.state ? cell.state : IN_PROGRESS)
                          : NOT_STARTED;
                    return (
                      <Box
                        key={c.key}
                        component="td"
                        data-testid={`grid-${row._id}-${c.key}`}
                        sx={{ ...bodyCell, ...rule(c.key), ...layer, px: 0.5, bgcolor: stripe, textAlign: 'center' }}
                      >
                        {cell ? (
                          /* A BUTTON, not a Select. Forty rows of six
                             multi-state columns mounted ~300 MUI Selects
                             on first paint — each one a FormControl, an
                             InputBase, a SelectInput and its popover
                             machinery, every one of them serialising its
                             own emotion styles. That was the wait. The
                             cell now renders the same words as a span and
                             the ONE menu below opens against whichever
                             cell was clicked. */
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={(e: React.MouseEvent<HTMLElement>) => setStateFor({ row, checkpoint: c, current, anchor: e.currentTarget })}
                            onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setStateFor({ row, checkpoint: c, current, anchor: e.currentTarget });
                              }
                            }}
                            sx={PICKER_SX}
                          >
                            <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {current === DONE ? (
                                <Stack direction="row" alignItems="center" gap={0.5} component="span">
                                  <DoneIcon sx={{ fontSize: 15, color: statusTokens.ok }} />
                                  <span>{c.doneLabel || 'Done'}</span>
                                </Stack>
                              ) : current === NOT_STARTED ? (
                                <Box component="span" sx={{ color: 'text.disabled' }}>Not started</Box>
                              ) : current === IN_PROGRESS ? 'In progress'
                                : ((c.states || []).find((st) => st.key === current)?.label || current)}
                            </Box>
                            <CaretIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
                          </Box>
                                                ) : (
                          <Typography variant="body2" color="text.disabled">–</Typography>
                        )}
                      </Box>
                    );
                  }

                  const dead = !cell || c.kind === 'field'
                    || (c.kind === 'document' && cell.status === 'done');
                  const tip = tipFor(row, c);
                  const td = (
                    <Box
                      component="td"
                      data-testid={`grid-${row._id}-${c.key}`}
                      onClick={(e: React.MouseEvent<HTMLElement>) => clickGlyph(row, c, e.currentTarget)}
                      sx={{
                        ...bodyCell, ...rule(c.key), ...layer, p: 0,
                        minWidth: CELL, maxWidth: CELL, bgcolor: stripe,
                        cursor: dead ? 'default' : 'pointer',
                        '&:hover': dead ? {} : { bgcolor: 'action.selected' },
                      }}
                    >
                      {/* Flex, not text-align: an inline SVG sits on the
                          text baseline and the tick rode high of centre. */}
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Glyph cell={cell} />
                      </Box>
                    </Box>
                  );
                  return tip
                    ? <Tooltip key={c.key} title={tip} placement="top" disableInteractive>{td}</Tooltip>
                    : <React.Fragment key={c.key}>{td}</React.Fragment>;
                })}

                <Box component="td" sx={{ ...bodyCell, px: 1.25, bgcolor: stripe, minWidth: 120, textAlign: 'center' }}>
                  <EditableCell
                    value={row.targetStartDate?.slice(0, 10)}
                    type="date"
                    placeholder="—"
                    render={(v) => <Typography variant="body2" noWrap>{formatDate(v)}</Typography>}
                    onSave={(next) => onEditField(row, { targetStartDate: next || null })}
                  />
                </Box>
                <Box component="td" sx={{ ...bodyCell, px: 1.25, bgcolor: stripe, minWidth: 220, maxWidth: 320, overflow: 'hidden' }}>
                  <EditableCell
                    value={row.notes}
                    placeholder="—"
                    onSave={(next) => onEditField(row, { notes: next })}
                  />
                </Box>
                <Box component="td" sx={{ ...bodyCell, px: 1.25, bgcolor: stripe, textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: stalled ? statusTokens.warn : 'text.secondary', fontWeight: stalled ? 700 : 400 }}
                  >
                    {row.daysSinceActivity == null ? '—'
                      : row.daysSinceActivity === 0 ? 'today'
                        : row.daysSinceActivity === 1 ? 'yesterday'
                          : `${row.daysSinceActivity} days ago`}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* The state menu — one, for every multi-state cell in the table.
          Its items carry role="option" because that is what they are: a
          choice among the states this step can be in. */}
      <Menu
        open={!!stateFor}
        anchorEl={stateFor?.anchor}
        onClose={() => setStateFor(null)}
        MenuListProps={{ role: 'listbox', dense: true }}
      >
        {stateFor && [
          <MenuItem
            key="__not_started__"
            role="option"
            selected={stateFor.current === NOT_STARTED}
            onClick={() => { pickState(stateFor.row, stateFor.checkpoint, NOT_STARTED, stateFor.anchor); setStateFor(null); }}
          >
            Not started
          </MenuItem>,
          // Present only while it IS the value: picking a real state
          // resolves the vagueness and it disappears.
          ...(stateFor.current === IN_PROGRESS
            ? [<MenuItem key="__in_progress__" role="option" disabled>In progress</MenuItem>]
            : []),
          ...(stateFor.checkpoint.states || []).map((st) => (
            <MenuItem
              key={st.key}
              role="option"
              selected={stateFor.current === st.key}
              onClick={() => { pickState(stateFor.row, stateFor.checkpoint, st.key, stateFor.anchor); setStateFor(null); }}
            >
              {st.label}
            </MenuItem>
          )),
          <MenuItem
            key="__done__"
            role="option"
            selected={stateFor.current === DONE}
            onClick={() => { pickState(stateFor.row, stateFor.checkpoint, DONE, stateFor.anchor); setStateFor(null); }}
          >
            {stateFor.checkpoint.kind === 'document' ? 'Upload the file…' : (stateFor.checkpoint.doneLabel || 'Done')}
          </MenuItem>,
        ]}
      </Menu>

      {/* The Status menu. One control, two jobs, kept visibly apart: a
          stage is a manual placement, a negative closes the pipeline. */}
      <Menu
        open={!!stageFor}
        anchorEl={stageFor?.anchor}
        onClose={() => setStageFor(null)}
        MenuListProps={{ role: 'listbox', dense: true }}
      >
        {stageFor && [
          <ListSubheader key="h1" sx={{ lineHeight: '28px', fontSize: 11 }}>Pipeline — moves on its own</ListSubheader>,
          ...STAGE_ORDER.map((st) => (
            <MenuItem
              key={st}
              role="option"
              sx={{ pl: 3 }}
              selected={stageFor.row.stage === st}
              onClick={() => { onSetStage(stageFor.row, st as CandidateStage); setStageFor(null); }}
            >
              {STAGE_LABEL[st]}
            </MenuItem>
          )),
          <ListSubheader key="h2" sx={{ lineHeight: '28px', fontSize: 11 }}>Close as…</ListSubheader>,
          ...(['hired', 'unresponsive', 'no_show', 'withdrew', 'declined'] as CandidateOutcome[]).map((o) => (
            <MenuItem
              key={o}
              role="option"
              sx={{ pl: 3 }}
              onClick={() => {
                const row = stageFor.row;
                setStageFor(null);
                if (o === 'hired') setHireAsk(row);
                else onClose(row, o);
              }}
            >
              {OUTCOME_LABEL[o]}
            </MenuItem>
          )),
        ]}
      </Menu>

      {/* The interview column's filter. Buckets of TIME rather than
          states: nobody filters a pipeline for "14:30", they filter it for
          who they are talking to today and who fell through. */}
      <Popover
        open={!!interviewMenu}
        anchorEl={interviewMenu}
        onClose={() => setInterviewMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 200 }}>
          <Stack direction="row" gap={0.5} sx={{ mb: 1 }}>
            <Chip
              size="small"
              label="All"
              variant={interviewFilter.size ? 'outlined' : 'filled'}
              onClick={() => setInterviewFilter(new Set())}
            />
            <Chip
              size="small"
              label="Booked"
              variant="outlined"
              onClick={() => setInterviewFilter(new Set(['missed', 'today', 'week', 'later']))}
            />
          </Stack>
          <Divider sx={{ my: 1 }} />
          {INTERVIEW_BUCKETS.map((bucket) => {
            const count = rows.filter((r) => interviewBucket(r) === bucket.id).length;
            return (
              <FormControlLabel
                key={bucket.id}
                sx={{ display: 'flex', ml: 0, mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={interviewFilter.has(bucket.id)}
                    onChange={() => setInterviewFilter((current) => {
                      const next = new Set(current);
                      if (next.has(bucket.id)) next.delete(bucket.id);
                      else next.add(bucket.id);
                      return next;
                    })}
                  />
                )}
                label={(
                  <Stack direction="row" alignItems="center" gap={1} sx={{ width: 150 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{bucket.label}</Typography>
                    <Typography variant="caption" color="text.disabled">{count}</Typography>
                  </Stack>
                )}
              />
            );
          })}
        </Box>
      </Popover>

      {/* The filter — quick chips, per-state checkboxes with counts. No
          Apply button: a tick lands immediately, and Cancel is closing
          the popover, which is how every other control here works. */}
      <Popover
        open={!!menuFor}
        anchorEl={menuFor?.anchor}
        onClose={() => setMenuFor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {menuFor && (() => {
          const key = menuFor.checkpoint.key;
          const current = filters[key] || new Set<Bucket>();
          const options: Array<{ id: Bucket; label: string }> = [
            { id: 'not_started', label: 'Not started' },
            ...(menuFor.checkpoint.states || []).map((st) => ({ id: `state:${st.key}`, label: st.label })),
            ...((menuFor.checkpoint.states || []).length ? [] : [{ id: 'in_progress', label: 'In progress' }]),
            { id: 'done', label: menuFor.checkpoint.doneLabel || 'Done' },
          ];
          const counts = new Map<Bucket, number>();
          rows.forEach((r) => {
            const b = bucketOf(r.checkpoints?.[key]);
            if (b) counts.set(b, (counts.get(b) || 0) + 1);
          });
          const setTo = (next: Set<Bucket>) => setFilters((f) => {
            const out = { ...f };
            if (next.size === 0 || next.size === options.length) delete out[key];
            else out[key] = next;
            return out;
          });
          const toggle = (id: Bucket) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setTo(next);
          };
          const notDone = new Set(options.filter((o) => o.id !== 'done').map((o) => o.id));
          return (
            <Stack sx={{ p: 1.5, width: 240 }} gap={1}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {checkpointLabel(menuFor.checkpoint)}
              </Typography>
              {sortButton({ checkpoint: key }, { desc: 'Done first', asc: 'Not started first' })}
              <Divider />
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                <Chip label="All" size="small" variant={current.size === 0 ? 'filled' : 'outlined'} onClick={() => setTo(new Set())} />
                <Chip label="Done" size="small" variant="outlined" onClick={() => setTo(new Set(['done']))} />
                <Chip label="Not done" size="small" variant="outlined" onClick={() => setTo(notDone)} />
              </Stack>
              <Stack>
                {options.map((option) => (
                  <FormControlLabel
                    key={option.id}
                    sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13, flex: 1 } }}
                    control={(
                      <Checkbox
                        size="small"
                        sx={{ py: 0.4 }}
                        checked={current.size === 0 || current.has(option.id)}
                        onChange={() => {
                          // From "everything" the first untick means
                          // "everything except this one".
                          if (current.size === 0) {
                            const next = new Set(options.map((o) => o.id));
                            next.delete(option.id);
                            setTo(next);
                          } else toggle(option.id);
                        }}
                      />
                    )}
                    label={(
                      <Stack direction="row" alignItems="center" gap={0.75} component="span" sx={{ width: '100%' }}>
                        {/* The same shapes the cells wear, so the menu
                            reads in the grid's own language. */}
                        {option.id === 'done'
                          ? <DoneIcon sx={{ fontSize: 14, color: statusTokens.ok }} />
                          : option.id === 'not_started'
                            ? <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', border: '2px solid', borderColor: 'divider', display: 'inline-block' }} />
                            : <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', border: '2px solid', borderColor: statusTokens.warn, display: 'inline-block' }} />}
                        <Box component="span" sx={{ flex: 1 }}>{option.label}</Box>
                        <Box component="span" sx={{ color: 'text.disabled' }}>{counts.get(option.id) || 0}</Box>
                      </Stack>
                    )}
                  />
                ))}
              </Stack>
            </Stack>
          );
        })()}
      </Popover>

      {/* Close everyone selected, as one outcome. */}
      <Menu
        open={!!closeMenuAnchor}
        anchorEl={closeMenuAnchor}
        onClose={() => setCloseMenuAnchor(null)}
      >
        {(['unresponsive', 'no_show', 'withdrew', 'declined'] as CandidateOutcome[]).map((o) => (
          <MenuItem
            key={o}
            onClick={() => {
              selectedRows.forEach((r) => onClose(r, o));
              setSelected(new Set());
              setCloseMenuAnchor(null);
            }}
          >
            {OUTCOME_LABEL[o]}
          </MenuItem>
        ))}
      </Menu>

      {/* Hired is a real ending with one optional extra. */}
      <Dialog open={!!hireAsk} onClose={() => setHireAsk(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{hireAsk ? `${candidateName(hireAsk)} is hired` : 'Hired'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Do you also want to invite them to the app as a driver? Inviting creates
            their account and files their paperwork into the vault. Marking hired
            without inviting just closes the record — their files are kept either way.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHireAsk(null)}>Cancel</Button>
          <Button
            color="inherit"
            onClick={() => { if (hireAsk) onClose(hireAsk, 'hired'); setHireAsk(null); }}
          >
            Just mark hired
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => { if (hireAsk) onOpen(hireAsk); setHireAsk(null); }}
          >
            Invite them — open their page
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deleting is for rows that should never have existed. */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {selected.size === 1 ? 'this candidate' : `${selected.size} candidates`}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Deleting erases the record entirely — it is for duplicates and test entries,
            rows that should never have existed. A real candidate who did not work out
            should be <strong>closed</strong> instead, which keeps the history.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            onClick={() => {
              onDelete(selectedRows);
              setSelected(new Set());
              setConfirmDelete(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Find somebody — the page header's search, moved to where every
          other column already keeps its controls. */}
      <Popover
        open={!!nameMenuAnchor}
        anchorEl={nameMenuAnchor}
        onClose={() => setNameMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack sx={{ p: 1.5, width: 240 }} gap={1}>
          <TextField
            autoFocus
            size="small"
            placeholder="Find somebody…"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setNameMenuAnchor(null); }}
          />
          {sortButton('name', { asc: 'A → Z', desc: 'Z → A' }, 'asc')}
          {nameQuery && (
            <Button size="small" color="inherit" onClick={() => setNameQuery('')}>Clear</Button>
          )}
        </Stack>
      </Popover>

      {/* Which stages to show — each option wearing its strip colour,
          the same code the legend and the bands speak. */}
      <Popover
        open={!!stageMenuAnchor}
        anchorEl={stageMenuAnchor}
        onClose={() => setStageMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack sx={{ p: 1.5, width: 230 }} gap={1}>
          {sortButton('stage', { asc: 'Outreach → Ready', desc: 'Ready → Outreach' }, 'asc')}
          <Divider />
          {STAGE_ORDER.map((stage) => {
            const count = rows.filter((r) => r.stage === stage).length;
            const checked = stageFilter.size === 0 || stageFilter.has(stage);
            return (
              <FormControlLabel
                key={stage}
                sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13, flex: 1 } }}
                control={(
                  <Checkbox
                    size="small"
                    sx={{ py: 0.4 }}
                    checked={checked}
                    onChange={() => setStageFilter((current) => {
                      const all = new Set(STAGE_ORDER);
                      const base = current.size === 0 ? all : new Set(current);
                      if (base.has(stage)) base.delete(stage);
                      else base.add(stage);
                      return base.size === STAGE_ORDER.length ? new Set() : base;
                    })}
                  />
                )}
                label={(
                  <Stack direction="row" alignItems="center" gap={0.75} component="span" sx={{ width: '100%' }}>
                    <Box component="span" sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: STAGE_COLOR[stage], display: 'inline-block' }} />
                    <Box component="span" sx={{ flex: 1 }}>{STAGE_LABEL[stage]}</Box>
                    <Box component="span" sx={{ color: 'text.disabled' }}>{count}</Box>
                  </Stack>
                )}
              />
            );
          })}
          {stageFilter.size > 0 && (
            <Button size="small" color="inherit" onClick={() => setStageFilter(new Set())}>Show every stage</Button>
          )}
        </Stack>
      </Popover>

      {/* A value checkpoint is a number somebody will need, not a tick. */}
      <Popover
        open={!!valueFor}
        anchorEl={valueFor?.anchor}
        onClose={() => setValueFor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {valueFor && (
          <Stack gap={1} sx={{ p: 1.5, width: 240 }}>
            <Typography variant="caption" color="text.secondary">
              {checkpointLabel(valueFor.checkpoint)} — {candidateName(valueFor.row)}
            </Typography>
            <TextField
              autoFocus
              size="small"
              type={valueFor.checkpoint.valueType === 'date' ? 'date' : 'text'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draft.trim()) {
                  onSetCheckpoint(valueFor.row, valueFor.checkpoint.key, { status: 'done', value: draft.trim() });
                  setValueFor(null);
                }
              }}
            />
            <Stack direction="row" justifyContent="space-between">
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  onSetCheckpoint(valueFor.row, valueFor.checkpoint.key, { status: 'not_started', state: null });
                  setValueFor(null);
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                disabled={!draft.trim()}
                onClick={() => {
                  onSetCheckpoint(valueFor.row, valueFor.checkpoint.key, { status: 'done', value: draft.trim() });
                  setValueFor(null);
                }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        )}
      </Popover>
    </Box>
    </>
  );
};

export default CandidateGrid;
